import { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "../components/Layout";
import { getAuthHeaders as getHeaders } from "../lib/auth";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, Loader2, ShieldAlert, TrendingDown, Package,
  Sparkles, Calendar, DollarSign, ChevronDown, ChevronRight, Camera,
  RefreshCw, Eye, X, Settings, Zap, Play, History, ToggleLeft, ToggleRight, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => n ? `${Number(n).toLocaleString("fr-FR")} F` : "—";

const SEV_COLORS = {
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-500", icon: "text-red-500" },
  urgent: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", badge: "bg-orange-500", icon: "text-orange-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-500", icon: "text-amber-500" },
  info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-500", icon: "text-blue-500" },
};

export default function StockIntelligencePage() {
  const [tab, setTab] = useState("expiry");
  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="stock-intelligence-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Alertes Stock Intelligentes</h1>
            <p className="text-xs text-slate-500">Preventions peremptions, invendus et recommandations IA</p>
          </div>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="stock-intel-tabs">
          {[
            { key: "expiry", label: "Alertes Peremption", icon: Calendar },
            { key: "unsold", label: "Produits Invendus", icon: TrendingDown },
            { key: "recommendations", label: "Recommandations IA", icon: Sparkles },
            { key: "auto", label: "Destruction Auto", icon: Zap },
            { key: "camera", label: "Scan Camera", icon: Camera },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold transition ${tab === t.key ? "bg-white text-amber-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "expiry" && <ExpiryAlertsTab />}
        {tab === "unsold" && <UnsoldProductsTab />}
        {tab === "recommendations" && <AIRecommendationsTab />}
        {tab === "auto" && <AutoDestructionTab />}
        {tab === "camera" && <CameraScanTab />}
      </div>
    </Layout>
  );
}


/* ═══════════ EXPIRY ALERTS TAB ═══════════ */
function ExpiryAlertsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/stock-intelligence/expiry-alerts`, { headers: getHeaders() });
        setData(res.data);
      } catch { toast.error("Erreur chargement alertes"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  if (!data) return null;

  const thresholds = ["1 mois", "2 mois", "3 mois", "6 mois"];

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="expiry-kpis">
        <KPI label="Deja expires" value={data.already_expired?.count || 0} color="red" icon={AlertTriangle} />
        <KPI label="< 1 mois" value={data.alerts?.["1 mois"]?.count || 0} color="red" icon={Clock} />
        <KPI label="< 2 mois" value={data.alerts?.["2 mois"]?.count || 0} color="orange" icon={Clock} />
        <KPI label="< 3 mois" value={data.alerts?.["3 mois"]?.count || 0} color="amber" icon={Clock} />
        <KPI label="Sans date" value={data.no_expiry_date?.count || 0} color="slate" icon={Package} />
      </div>

      {/* Already Expired */}
      {data.already_expired?.count > 0 && (
        <AlertSection
          title={`Produits expires (${data.already_expired.count})`}
          severity="critical"
          products={data.already_expired.products}
          totalValue={data.already_expired.total_value}
          expanded={expanded === "expired"}
          onToggle={() => setExpanded(expanded === "expired" ? null : "expired")}
          dateField="days_expired"
          dateLabel="Expire depuis"
          dateSuffix="j"
        />
      )}

      {/* Thresholds */}
      {thresholds.map(label => {
        const alert = data.alerts?.[label];
        if (!alert || alert.count === 0) return null;
        const sev = label === "1 mois" ? "critical" : label === "2 mois" ? "urgent" : label === "3 mois" ? "warning" : "info";
        return (
          <AlertSection key={label}
            title={`Expire dans ${label} (${alert.count})`}
            severity={sev}
            products={alert.products}
            totalValue={alert.total_value}
            expanded={expanded === label}
            onToggle={() => setExpanded(expanded === label ? null : label)}
            dateField="days_remaining"
            dateLabel="Dans"
            dateSuffix="j"
          />
        );
      })}

      {data.at_risk_count === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
          <p className="text-emerald-700 font-semibold">Aucun produit a risque de peremption</p>
        </div>
      )}
    </div>
  );
}


/* ═══════════ UNSOLD PRODUCTS TAB ═══════════ */
function UnsoldProductsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/stock-intelligence/unsold-products`, { headers: getHeaders() });
        setData(res.data);
      } catch { toast.error("Erreur chargement invendus"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPI label="Produits invendus (+90j)" value={data.total_count} color="orange" icon={TrendingDown} />
        <KPI label="Valeur immobilisee" value={fmt(data.total_value)} color="red" icon={DollarSign} isText />
        <KPI label="Seuil" value={`${data.threshold_days} jours`} color="slate" icon={Clock} isText />
      </div>

      {data.unsold_products?.length > 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-800">Produits sans vente depuis plus de {data.threshold_days} jours</h3>
          </div>
          <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Produit</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Classe</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Qte</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Prix</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Jours invendu</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Peremption</th>
                </tr>
              </thead>
              <tbody>
                {data.unsold_products.map((p, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-3 py-2 font-semibold text-slate-800">{p.name}</td>
                    <td className="px-3 py-2 text-slate-600">{p.pharma_class || "—"}</td>
                    <td className="px-3 py-2 text-center">{p.quantity}</td>
                    <td className="px-3 py-2 text-right">{fmt(p.unit_price)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.days_unsold > 180 ? "bg-red-100 text-red-700" : p.days_unsold > 90 ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>
                        {p.days_unsold}j
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{p.expiry_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
          <p className="text-emerald-700 font-semibold">Aucun produit invendu detecte</p>
        </div>
      )}
    </div>
  );
}


/* ═══════════ AI RECOMMENDATIONS TAB ═══════════ */
function AIRecommendationsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReco = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/stock-intelligence/ai-recommendations`, {}, { headers: getHeaders() });
      setData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  }, []);

  const priorityColors = { haute: "bg-red-100 text-red-700", moyenne: "bg-amber-100 text-amber-700", basse: "bg-blue-100 text-blue-700" };
  const catIcons = { promotion: DollarSign, exchange: RefreshCw, return: Package, order: Package, organization: Eye };

  return (
    <div className="space-y-4">
      {!data && !loading && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-8 text-center">
          <Sparkles className="w-12 h-12 text-violet-400 mx-auto mb-3" />
          <h3 className="font-bold text-slate-800 mb-1">Recommandations IA</h3>
          <p className="text-sm text-slate-600 mb-4">L'IA analyse votre stock et propose des actions concretes pour reduire les pertes</p>
          <button onClick={loadReco} data-testid="load-reco-btn"
            className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-lg transition">
            <Sparkles className="w-4 h-4 inline mr-2" /> Analyser mon stock
          </button>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500 mb-3" />
          <p className="text-sm text-slate-600">L'IA analyse votre stock...</p>
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-800">Diagnostic IA</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{data.expiring_count} a risque · {data.unsold_count} invendus</span>
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${data.risk_score > 60 ? "bg-red-100 text-red-700" : data.risk_score > 30 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  Score risque: {data.risk_score}/100
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600">{data.summary}</p>
          </div>

          {/* Recommendations */}
          <div className="space-y-3">
            {(data.recommendations || []).map((r, i) => {
              const CatIcon = catIcons[r.category] || Package;
              return (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition">
                  <div className="flex items-start gap-3">
                    <CatIcon className="w-5 h-5 text-violet-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-bold text-slate-800">{r.title}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${priorityColors[r.priority] || priorityColors.basse}`}>{r.priority}</span>
                      </div>
                      <p className="text-xs text-slate-600">{r.description}</p>
                      {r.expected_impact && <p className="text-[10px] text-emerald-600 mt-1 font-medium">Impact: {r.expected_impact}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Preventive Measures */}
          {data.preventive_measures?.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-emerald-800 mb-2">Mesures preventives</h3>
              <ul className="space-y-1">
                {data.preventive_measures.map((m, i) => (
                  <li key={i} className="text-xs text-emerald-700 flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">&#10003;</span> {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button onClick={loadReco} className="text-xs text-violet-600 hover:text-violet-700 font-semibold">
            <RefreshCw className="w-3 h-3 inline mr-1" /> Relancer l'analyse
          </button>
        </>
      )}
    </div>
  );
}


/* ═══════════ CAMERA SCAN TAB ═══════════ */
function CameraScanTab() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreaming(true);
      }
    } catch {
      toast.error("Impossible d'acceder a la camera. Verifiez les permissions.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
      setStreaming(false);
    }
  }, []);

  const captureAndScan = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      setScanning(true);
      try {
        const fd = new FormData();
        fd.append("file", new File([blob], "capture.jpg", { type: "image/jpeg" }));
        const res = await axios.post(`${API}/scanner/scan-product`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
        setResult(res.data);
        setEditFields(res.data.product || {});
        toast.success("Produit scanne !");
        stopCamera();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur scan");
      } finally { setScanning(false); }
    }, "image/jpeg", 0.85);
  }, [stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleValidate = async () => {
    if (!result?.scan_id) return;
    setSaving(true);
    try {
      await axios.post(`${API}/scanner/validate-product`, {
        scan_id: result.scan_id, product_index: 0,
        name: editFields.name || "", form: editFields.form || "", dosage: editFields.dosage || "",
        lot_number: editFields.lot_number || "", expiry_date: editFields.expiry_date || "",
        laboratory: editFields.laboratory || "", barcode: editFields.barcode || "",
        pharma_class: editFields.pharma_class || "", quantity: Number(editFields.quantity_per_box) || 1,
      }, { headers: getHeaders() });
      toast.success("Produit enregistre !");
      setResult(null); setEditFields({});
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const updateField = (k, v) => setEditFields(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-4">
      {!result && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Camera className="w-4 h-4 text-violet-500" /> Scanner Camera Temps Reel</h3>
            {streaming ? (
              <button onClick={stopCamera} className="text-xs text-red-600 font-semibold"><X className="w-3 h-3 inline mr-1" />Arreter</button>
            ) : (
              <button onClick={startCamera} data-testid="start-camera-btn" className="px-3 py-1.5 bg-violet-600 text-white text-xs font-bold rounded-lg hover:bg-violet-700 transition">
                <Camera className="w-3 h-3 inline mr-1" /> Demarrer la camera
              </button>
            )}
          </div>

          <div className="relative bg-black aspect-video flex items-center justify-center">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            {!streaming && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90">
                <Camera className="w-12 h-12 text-slate-600 mb-2" />
                <p className="text-sm text-slate-500">Cliquez sur "Demarrer la camera"</p>
                <p className="text-xs text-slate-600 mt-1">Pointez vers une boite de medicament</p>
              </div>
            )}
            {scanning && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-violet-400 mx-auto mb-2" />
                  <p className="text-sm text-white font-semibold">Analyse IA en cours...</p>
                </div>
              </div>
            )}
          </div>

          {streaming && !scanning && (
            <div className="p-4 flex justify-center">
              <button onClick={captureAndScan} data-testid="capture-btn"
                className="px-8 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl text-sm transition shadow-lg">
                <Camera className="w-4 h-4 inline mr-2" /> Capturer et analyser
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="camera-scan-result">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-500" /> Resultat du scan</h3>
            <button onClick={() => { setResult(null); setEditFields({}); }} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {["name", "dci", "pharma_class", "form", "dosage", "laboratory", "lot_number", "expiry_date", "barcode", "indication"].map(k => (
              <div key={k}>
                <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{k.replace(/_/g, " ")}</label>
                <input value={editFields[k] || ""} onChange={e => updateField(k, e.target.value)} data-testid={`cam-field-${k}`}
                  className="w-full mt-0.5 px-2 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:border-violet-400 outline-none" />
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-slate-100 flex gap-2 justify-end">
            <button onClick={() => { setResult(null); setEditFields({}); startCamera(); }} className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">Re-scanner</button>
            <button onClick={handleValidate} disabled={saving || !editFields.name} data-testid="cam-validate-btn"
              className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null} Valider et enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════ SHARED COMPONENTS ═══════════ */
function KPI({ label, value, color, icon: Icon, isText }) {
  const colors = { red: "bg-red-50 border-red-200 text-red-700", orange: "bg-orange-50 border-orange-200 text-orange-700", amber: "bg-amber-50 border-amber-200 text-amber-700", slate: "bg-slate-50 border-slate-200 text-slate-700", emerald: "bg-emerald-50 border-emerald-200 text-emerald-700" };
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.slate}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</span>
      </div>
      <p className={`${isText ? "text-sm" : "text-xl"} font-bold`}>{value}</p>
    </div>
  );
}

function AlertSection({ title, severity, products, totalValue, expanded, onToggle, dateField, dateLabel, dateSuffix }) {
  const c = SEV_COLORS[severity] || SEV_COLORS.info;
  return (
    <div className={`rounded-2xl border ${c.border} ${c.bg} overflow-hidden`}>
      <button onClick={onToggle} className="w-full p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-5 h-5 ${c.icon}`} />
          <span className={`text-sm font-bold ${c.text}`}>{title}</span>
          {totalValue > 0 && <span className="text-xs opacity-70">{fmt(totalValue)} de valeur</span>}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
      </button>
      {expanded && products?.length > 0 && (
        <div className="border-t border-white/50 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/60">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Produit</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Classe</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600">Qte</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600">{dateLabel}</th>
              </tr>
            </thead>
            <tbody>
              {products.slice(0, 50).map((p, i) => (
                <tr key={i} className="border-b border-white/30 hover:bg-white/40">
                  <td className="px-3 py-2 font-semibold text-slate-800">{p.name}</td>
                  <td className="px-3 py-2 text-slate-600">{p.pharma_class || "—"}</td>
                  <td className="px-3 py-2 text-center">{p.quantity}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.text} bg-white/60`}>
                      {p[dateField]}{dateSuffix}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function AutoDestructionTab() {
  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [daysAfter, setDaysAfter] = useState(30);
  const [notifyBefore, setNotifyBefore] = useState(7);
  const [minProducts, setMinProducts] = useState(1);
  const [enabled, setEnabled] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cfgRes, pendingRes, histRes] = await Promise.all([
        axios.get(`${API}/stock-intelligence/auto-destruction/config`, { headers: getHeaders() }),
        axios.get(`${API}/stock-intelligence/auto-destruction/pending`, { headers: getHeaders() }),
        axios.get(`${API}/stock-intelligence/auto-destruction/history`, { headers: getHeaders() }),
      ]);
      const cfg = cfgRes.data;
      setConfig(cfg);
      setEnabled(cfg.enabled || false);
      setDaysAfter(cfg.days_after_expiry || 30);
      setNotifyBefore(cfg.notify_before_days || 7);
      setMinProducts(cfg.min_products || 1);
      setPending(pendingRes.data);
      setHistory(histRes.data.history || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/stock-intelligence/auto-destruction/config`, {
        enabled, days_after_expiry: daysAfter, notify_before_days: notifyBefore, min_products: minProducts,
      }, { headers: getHeaders() });
      toast.success("Configuration sauvegardee");
      load();
    } catch { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  const triggerDestruction = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/stock-intelligence/auto-destruction/trigger`, {}, { headers: getHeaders() });
      if (res.data.triggered) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.reason);
      }
      setShowConfirm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setTriggering(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;

  const eligibleCount = pending?.total_eligible || 0;
  const warningCount = pending?.total_warning || 0;
  const totalValue = pending?.total_value || 0;

  return (
    <div className="space-y-4" data-testid="auto-destruction-tab">
      {/* Config Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="auto-destruct-config">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold text-slate-800">Configuration du seuil automatique</h3>
          </div>
          <button onClick={() => { setEnabled(!enabled); }} data-testid="auto-destruct-toggle"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${enabled ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>
            {enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            {enabled ? "Active" : "Desactive"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Jours apres peremption</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="365" value={daysAfter} onChange={e => setDaysAfter(parseInt(e.target.value) || 30)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:ring-2 focus:ring-amber-300 focus:border-amber-400" data-testid="days-after-input" />
              <span className="text-xs text-slate-400 whitespace-nowrap">jours</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Destruction declenchee X jours apres la date de peremption</p>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Alerte preventive</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="30" value={notifyBefore} onChange={e => setNotifyBefore(parseInt(e.target.value) || 7)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:ring-2 focus:ring-amber-300 focus:border-amber-400" data-testid="notify-before-input" />
              <span className="text-xs text-slate-400 whitespace-nowrap">jours avant</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Notification envoyee avant le declenchement</p>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Minimum de produits</label>
            <input type="number" min="1" max="100" value={minProducts} onChange={e => setMinProducts(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:ring-2 focus:ring-amber-300 focus:border-amber-400" data-testid="min-products-input" />
            <p className="text-[10px] text-slate-400 mt-1">Lot minimum pour declencher la destruction</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={saveConfig} disabled={saving} data-testid="save-config-btn"
            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
            Sauvegarder
          </button>
          <span className="text-[10px] text-slate-400">
            Le scan automatique est execute chaque jour a 9h00
          </span>
        </div>
      </div>

      {/* Status Cards */}
      {enabled && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className={`rounded-xl border p-4 ${eligibleCount > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`} data-testid="eligible-count-card">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className={`w-4 h-4 ${eligibleCount > 0 ? "text-red-500" : "text-slate-400"}`} />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Eligible a la destruction</span>
            </div>
            <p className={`text-2xl font-bold ${eligibleCount > 0 ? "text-red-700" : "text-slate-600"}`}>{eligibleCount}</p>
            <p className="text-[10px] text-slate-400">produit(s) &gt; {daysAfter}j apres peremption</p>
          </div>
          <div className={`rounded-xl border p-4 ${warningCount > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid="warning-count-card">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className={`w-4 h-4 ${warningCount > 0 ? "text-amber-500" : "text-slate-400"}`} />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">En alerte preventive</span>
            </div>
            <p className={`text-2xl font-bold ${warningCount > 0 ? "text-amber-700" : "text-slate-600"}`}>{warningCount}</p>
            <p className="text-[10px] text-slate-400">produit(s) bientot au seuil</p>
          </div>
          <div className="rounded-xl border p-4 bg-slate-50 border-slate-200" data-testid="value-card">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Valeur totale a detruire</span>
            </div>
            <p className="text-2xl font-bold text-slate-700">{fmt(totalValue)}</p>
            <p className="text-[10px] text-slate-400">valeur des produits eligibles</p>
          </div>
        </div>
      )}

      {/* Eligible Products List */}
      {enabled && eligibleCount > 0 && (
        <div className="bg-white rounded-xl border border-red-200 shadow-sm" data-testid="eligible-products-list">
          <div className="p-4 border-b border-red-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-500" />
              <h3 className="text-sm font-bold text-red-800">{eligibleCount} produit(s) pret(s) pour destruction automatique</h3>
            </div>
            <button onClick={() => setShowConfirm(true)} data-testid="trigger-destruction-btn"
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition flex items-center gap-2">
              <Play className="w-3 h-3" /> Declencher la destruction
            </button>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-red-50">
                <tr className="text-xs text-red-700 border-b border-red-100">
                  <th className="text-left py-2 px-3">Produit</th>
                  <th className="text-left py-2 px-3">Categorie</th>
                  <th className="text-right py-2 px-3">Qte</th>
                  <th className="text-right py-2 px-3">Valeur</th>
                  <th className="text-right py-2 px-3">Perime depuis</th>
                </tr>
              </thead>
              <tbody>
                {(pending?.eligible_products || []).map(p => (
                  <tr key={p.id} className="border-b border-red-50 hover:bg-red-50/50">
                    <td className="py-2 px-3 text-slate-800 font-medium">{p.name}</td>
                    <td className="py-2 px-3 text-slate-500 text-xs">{p.category || "—"}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{p.quantity}</td>
                    <td className="py-2 px-3 text-right text-red-700 font-medium">{fmt(p.value)}</td>
                    <td className="py-2 px-3 text-right">
                      <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{p.days_past_expiry}j</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Warning Products */}
      {enabled && warningCount > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-4" data-testid="warning-products-list">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-bold text-amber-800">{warningCount} produit(s) en alerte preventive</h3>
          </div>
          <div className="space-y-1">
            {(pending?.warning_products || []).map(p => (
              <div key={p.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-amber-50/50 text-xs">
                <span className="text-slate-700 font-medium">{p.name}</span>
                <span className="text-amber-600 font-semibold">{p.days_until_auto_destruction}j avant seuil</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {enabled && eligibleCount === 0 && warningCount === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="no-eligible-products">
          <Package className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-700">Aucun produit ne depasse le seuil de {daysAfter} jours</p>
          <p className="text-xs text-emerald-500 mt-1">Le systeme verifie automatiquement chaque jour a 9h00</p>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="auto-destruct-history">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold text-slate-700">Historique des destructions automatiques</h3>
          </div>
          <div className="space-y-2">
            {history.slice(0, 10).map(h => (
              <div key={h.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-xs">
                <div>
                  <span className="text-slate-700 font-medium">{h.product_count} produit(s)</span>
                  <span className="text-slate-400 mx-2">—</span>
                  <span className="text-slate-500">{new Date(h.triggered_at).toLocaleDateString("fr-FR")} {new Date(h.triggered_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <span className="text-red-600 font-semibold">{fmt(h.total_value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">Confirmer la destruction</h3>
                <p className="text-xs text-slate-500">Cette action est irreversible</p>
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 mb-4 text-sm text-red-800">
              <p className="font-semibold mb-1">{eligibleCount} produit(s) seront mis en destruction :</p>
              <p>Valeur totale : <strong>{fmt(totalValue)}</strong></p>
              <p className="text-xs text-red-600 mt-1">Une demande de destruction sera creee et les produits seront marques en attente.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition" data-testid="cancel-trigger-btn">
                Annuler
              </button>
              <button onClick={triggerDestruction} disabled={triggering} data-testid="confirm-trigger-btn"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
                {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
